import type { OrgChartPropertyOptionWithData } from "src/components/OrgChart/types";
import {
  resolveOverlayDisplayLabel,
  type AttributeLabelsByKey,
} from "src/pages/OrgDesignSettings/attributeLabels";
import {
  isPositionOverlayAttributeKey,
  isTeamDetailsChromeKey,
  overlayEntryForCardEntity,
} from "./overlayAttributeCatalog";
import { filterOverlayCatalogOptionsByVisibleEntities } from "./overlayAttributes";
import {
  isOverlayBagKey,
  parseOverlayBagKey,
  type PersonOverlayAttributeKey,
  type PersonOverlayAttributeKind,
} from "./personOverlayAttributeKeys";
import {
  personOverlayRegistry,
  type PersonOverlayValueSource,
} from "./personOverlayRegistry";
import { TEAM_OVERLAY_ATTRIBUTES_ENABLED } from "./teamOverlayFields";

export type { PersonOverlayAttributeKey } from "./personOverlayAttributeKeys";
export type { PersonOverlayValueSource } from "./personOverlayRegistry";
export { TEAM_OVERLAY_ATTRIBUTES_ENABLED };

/** Curated scalar overlay keys (Details edit + Properties share these ids). */
export const PERSON_SCALAR_OVERLAY_KEY = {
  COUNTRY: "country",
  CITY: "city",
  STATE: "state",
  ADDRESS: "address",
  COMPANY: "company",
  TIMEZONE: "timezone",
  EMAIL: "email",
  BANDS: "bands",
  SPAN: "span",
  LAYERS: "layers",
  DIRECT_WORKFORCE: "directWorkforce",
  FTE: "fte",
} as const;

export type PersonScalarOverlayKey =
  (typeof PERSON_SCALAR_OVERLAY_KEY)[keyof typeof PERSON_SCALAR_OVERLAY_KEY];

export type OverlayAttributeEntityKind = "person" | "team";

export { isPositionOverlayAttributeKey };

const isOptionalStringField = (value: unknown): boolean =>
  value === undefined || value === null || typeof value === "string";

const isOptionalBooleanField = (value: unknown): boolean =>
  value === undefined || value === null || typeof value === "boolean";

const isOverlayAttributeKind = (
  value: unknown
): value is PersonOverlayAttributeKind => value === "SCALAR" || value === "BAG";

const isPersonOverlayAttributeKey = (
  value: unknown
): value is PersonOverlayAttributeKey => {
  if (typeof value !== "object" || value === null) {
    return false;
  }
  if (
    !("key" in value) ||
    typeof value.key !== "string" ||
    value.key.length === 0
  ) {
    return false;
  }
  if (!("kind" in value) || !isOverlayAttributeKind(value.kind)) {
    return false;
  }
  if ("label" in value && !isOptionalStringField(value.label)) {
    return false;
  }
  if (
    "powerOrAdminOnly" in value &&
    !isOptionalBooleanField(value.powerOrAdminOnly)
  ) {
    return false;
  }
  return true;
};

/** `*.graphql` modules type as `{}`; validate at the query boundary. */
export const personOverlayAttributeKeysFromQueryData = (
  data: unknown
): PersonOverlayAttributeKey[] => {
  if (typeof data !== "object" || data === null) {
    return [];
  }
  if (!("personOverlayAttributeKeys" in data)) {
    return [];
  }
  const keys = data.personOverlayAttributeKeys;
  if (!Array.isArray(keys)) {
    return [];
  }
  return keys.filter(isPersonOverlayAttributeKey);
};

const POWER_OR_ADMIN_ONLY_OVERLAY_KEYS = new Set(["bands", "dailyRate"]);

const keyMetadataByKey = (
  catalog: PersonOverlayAttributeKey[]
): Map<string, PersonOverlayAttributeKey> =>
  new Map(catalog.map((entry) => [entry.key, entry]));

const isPersonScalarOverlayKey = (value: string): boolean =>
  /^[a-z][a-zA-Z0-9]*$/.test(value);

export const isOverlayAttributeKeyFormat = (
  entityKind: OverlayAttributeEntityKind,
  value: string
): boolean => {
  switch (entityKind) {
    case "team":
      if (isTeamDetailsChromeKey(value)) {
        return false;
      }
      return (
        isOverlayBagKey(value) ||
        isPersonScalarOverlayKey(value) ||
        /^team\.[a-z][a-zA-Z0-9]*$/.test(value)
      );
    case "person":
      return (
        isOverlayBagKey(value) ||
        isPersonScalarOverlayKey(value) ||
        isPositionOverlayAttributeKey(value)
      );
    default: {
      const _exhaustive: never = entityKind;
      return _exhaustive;
    }
  }
};

export const isOverlayAttributeVisibleToUser = ({
  attributeKey,
  isDirectoryUser = false,
  catalog = [],
}: {
  attributeKey: string;
  isDirectoryUser?: boolean | undefined;
  catalog?: PersonOverlayAttributeKey[] | undefined;
}): boolean => {
  const entry = keyMetadataByKey(catalog).get(attributeKey);
  if (entry) {
    return !(isDirectoryUser && entry.powerOrAdminOnly);
  }
  if (isOverlayBagKey(attributeKey)) {
    return !isDirectoryUser;
  }
  if (isDirectoryUser && POWER_OR_ADMIN_ONLY_OVERLAY_KEYS.has(attributeKey)) {
    return false;
  }
  return true;
};

export const getOverlayAttributeLabel = (
  attributeKey: string,
  catalog: PersonOverlayAttributeKey[] = []
): string | null => {
  const entry = keyMetadataByKey(catalog).get(attributeKey);
  if (entry?.label?.trim()) {
    return entry.label.trim();
  }
  if (isOverlayBagKey(attributeKey)) {
    return parseOverlayBagKey(attributeKey)?.attributeType ?? null;
  }
  return attributeKey;
};

const ATTRIBUTE_LIST_KIND = [
  ["peopleAttributes", "person"],
  ["teamsAttributes", "team"],
] as const satisfies ReadonlyArray<
  readonly ["peopleAttributes" | "teamsAttributes", OverlayAttributeEntityKind]
>;

const getSelectedOverlayAttributeKeys = (
  properties:
    | {
        peopleAttributes?: OrgChartPropertyOptionWithData[] | null;
        teamsAttributes?: OrgChartPropertyOptionWithData[] | null;
      }
    | null
    | undefined,
  options?: {
    isDirectoryUser?: boolean | undefined;
    catalog?: PersonOverlayAttributeKey[] | undefined;
  }
): string[] => {
  const selected: string[] = [];
  if (!properties) {
    return selected;
  }
  const isDirectoryUser = options?.isDirectoryUser ?? false;
  const catalog = options?.catalog ?? [];

  for (const [listKey, entityKind] of ATTRIBUTE_LIST_KIND) {
    const list = properties[listKey];
    if (!Array.isArray(list)) {
      continue;
    }
    for (const item of list) {
      if (
        item?.data?.isSelected &&
        item.value &&
        isOverlayAttributeKeyFormat(entityKind, item.value) &&
        isOverlayAttributeVisibleToUser({
          attributeKey: item.value,
          isDirectoryUser,
          catalog,
        })
      ) {
        selected.push(item.value);
      }
    }
  }
  return selected;
};

export type PersonOverlayQueryVariables = {
  overlayAttributeKeys: string[];
};

export const getPersonOverlayQueryVariables = (
  properties:
    | {
        peopleAttributes?: OrgChartPropertyOptionWithData[] | null;
        teamsAttributes?: OrgChartPropertyOptionWithData[] | null;
      }
    | null
    | undefined,
  options?: {
    isDirectoryUser?: boolean | undefined;
    enabled?: boolean | undefined;
    catalog?: PersonOverlayAttributeKey[] | undefined;
  }
): PersonOverlayQueryVariables => {
  if (options?.enabled === false) {
    return { overlayAttributeKeys: [] };
  }
  return {
    overlayAttributeKeys: getSelectedOverlayAttributeKeys(properties, options),
  };
};

export const getPersonOverlayQueryVariablesKey = (
  variables: PersonOverlayQueryVariables
): string => variables.overlayAttributeKeys.slice().sort().join("|");

/** Overlay Properties picker: full catalog, optionally hiding empty keys on canvas. */
export const getOverlayAttributePickerOptions = ({
  catalog,
  entityKind,
  people = [],
  alwaysIncludeKeys = [],
  hideEmptyOptions = false,
  labelsByKey = {},
}: {
  catalog: PersonOverlayAttributeKey[];
  entityKind: OverlayAttributeEntityKind;
  people?: PersonOverlayValueSource[];
  alwaysIncludeKeys?: string[];
  hideEmptyOptions?: boolean;
  labelsByKey?: AttributeLabelsByKey;
}): Array<{ value: string; label: string }> =>
  filterOverlayCatalogOptionsByVisibleEntities({
    options: getOverlayAttributeCatalogOptions({
      catalog,
      entityKind,
      labelsByKey,
    }),
    entities: people,
    registry: personOverlayRegistry,
    alwaysIncludeKeys,
    hideEmptyOptions,
  });

/** People-attribute options allowed in Properties and on card overlays. */
export const getAllowedPersonOverlayAttributeOptions = ({
  catalog,
  overlayFilterPeople,
  isDirectoryUser = false,
  labelsByKey = {},
}: {
  catalog: PersonOverlayAttributeKey[];
  overlayFilterPeople: PersonOverlayValueSource[];
  isDirectoryUser?: boolean;
  labelsByKey?: AttributeLabelsByKey;
}): Array<{ value: string; label: string }> => {
  if (catalog.length === 0) {
    return [];
  }
  return getOverlayAttributePickerOptions({
    catalog,
    entityKind: "person",
    people: overlayFilterPeople,
    hideEmptyOptions: true,
    labelsByKey,
  }).filter((option) =>
    isOverlayAttributeVisibleToUser({
      attributeKey: option.value,
      isDirectoryUser,
      catalog,
    })
  );
};

export const getOverlayAttributeCatalogOptions = ({
  catalog,
  entityKind,
  labelsByKey = {},
}: {
  catalog: PersonOverlayAttributeKey[];
  entityKind: OverlayAttributeEntityKind;
  labelsByKey?: AttributeLabelsByKey;
}): Array<{ value: string; label: string }> => {
  const toOption = (entry: PersonOverlayAttributeKey) => ({
    value: entry.key,
    label: resolveOverlayDisplayLabel({
      key: entry.key,
      catalogLabel: entry.label,
      labelsByKey,
    }),
  });
  switch (entityKind) {
    case "team": {
      if (!TEAM_OVERLAY_ATTRIBUTES_ENABLED) {
        return [];
      }
      return catalog
        .filter((entry) => overlayEntryForCardEntity(entry, "team"))
        .map(toOption);
    }
    case "person":
      return catalog
        .filter(
          (entry) =>
            (entry.kind === "SCALAR" || entry.kind === "BAG") &&
            overlayEntryForCardEntity(entry, "person")
        )
        .map(toOption)
        .sort((a, b) => a.label.localeCompare(b.label));
    default: {
      const _exhaustive: never = entityKind;
      return _exhaustive;
    }
  }
};
